import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bell,
  ChevronUp,
  AlertTriangle,
  DollarSign,
  TrendingDown,
  UserX,
  Activity,
  MapPin,
  Unplug,
  TrendingUp,
  Gauge,
  Check,
  CheckCheck,
  X,
  Zap,
  Trophy,
  TriangleAlert,
  Video,
  Package,
  ShoppingCart,
  Handshake,
  Phone,
  Store,
  RefreshCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface QuickAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  variant: "danger" | "warning" | "success";
  onExecute: () => void;
  isPending?: boolean;
}

export interface ActivityItem {
  id: string;
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
  recommendation?: string;
  time: string;
  isRead?: boolean;
  severityBadge?: { label: string; className: string };
  metricDisplay?: { actual: string; target: string };
  quickActions?: QuickAction[];
  feedbackPanel?: React.ReactNode;
  analysisPanel?: React.ReactNode;
  onMarkRead?: () => void;
  onDismiss?: () => void;
}

interface ActivityDropdownProps {
  items: ActivityItem[];
  label?: string;
  sublabel?: string;
  onMarkAllRead?: () => void;
  hasUnread?: boolean;
}

const VARIANT_STYLES: Record<string, string> = {
  danger: "bg-red-500/15 text-red-400 hover:bg-red-500/25 border-red-500/20",
  warning: "bg-yellow-500/15 text-yellow-400 hover:bg-yellow-500/25 border-yellow-500/20",
  success: "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border-emerald-500/20",
};

function QuickActionsBar({ actions }: { actions: QuickAction[] }) {
  const [confirmId, setConfirmId] = useState<string | null>(null);

  return (
    <div className="flex flex-wrap gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
      {actions.map((action) => {
        const isConfirming = confirmId === action.id;
        const style = VARIANT_STYLES[action.variant] || VARIANT_STYLES.warning;

        if (isConfirming) {
          return (
            <div key={action.id} className="flex items-center gap-1">
              <button
                onClick={() => {
                  action.onExecute();
                  setConfirmId(null);
                }}
                disabled={action.isPending}
                className={cn(
                  "flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-all",
                  style,
                  action.isPending && "opacity-50 cursor-not-allowed"
                )}
              >
                <Zap className="w-3 h-3" />
                {action.isPending ? "Wykonuję..." : "Potwierdź"}
              </button>
              <button
                onClick={() => setConfirmId(null)}
                className="px-1.5 py-1 rounded-lg text-white/25 hover:text-white/50 hover:bg-white/5 transition-colors text-[11px]"
              >
                Anuluj
              </button>
            </div>
          );
        }

        return (
          <button
            key={action.id}
            onClick={() => setConfirmId(action.id)}
            disabled={action.isPending}
            className={cn(
              "flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-all",
              style,
              action.isPending && "opacity-50 cursor-not-allowed"
            )}
          >
            {action.icon || <Zap className="w-3 h-3" />}
            {action.label}
          </button>
        );
      })}
    </div>
  );
}

export function ActivityDropdown({
  items,
  label,
  sublabel,
  onMarkAllRead,
  hasUnread,
}: ActivityDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const displayLabel = label ?? `${items.length} powiadomień`;
  const displaySublabel = sublabel ?? "Co się dzieje w Twoich kampaniach";

  return (
    <div
      className={cn(
        "w-full rounded-2xl overflow-hidden cursor-pointer select-none",
        "bg-[#0d1117] border border-white/[0.08]",
        "shadow-xl shadow-black/40",
        "transition-all duration-500 ease-[cubic-bezier(0.4,_0,_0.2,_1)]",
        isOpen ? "rounded-3xl" : "rounded-2xl"
      )}
      onClick={() => setIsOpen(!isOpen)}
    >
      {/* Header */}
      <div className="flex items-center gap-4 p-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/5 border border-white/[0.06] transition-colors duration-300 flex-shrink-0">
          <Bell className="h-5 w-5 text-amber-400" />
        </div>
        <div className="flex-1 overflow-hidden">
          <h3 className="text-base font-semibold text-white">{displayLabel}</h3>
          <p
            className={cn(
              "text-sm text-white/40",
              "transition-all duration-500 ease-[cubic-bezier(0.4,_0,_0.2,_1)]",
              isOpen ? "opacity-0 max-h-0 mt-0" : "opacity-100 max-h-6 mt-0.5"
            )}
          >
            {displaySublabel}
          </p>
        </div>
        {isOpen && onMarkAllRead && hasUnread && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMarkAllRead();
            }}
            className="flex items-center gap-1.5 text-white/40 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors text-[11px] select-none"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            {t("monitoringAlerts.markAll")}
          </button>
        )}
        <div className="flex h-8 w-8 items-center justify-center">
          <ChevronUp
            className={cn(
              "h-5 w-5 text-white/30 transition-transform duration-500 ease-[cubic-bezier(0.4,_0,_0.2,_1)]",
              isOpen ? "rotate-0" : "rotate-180"
            )}
          />
        </div>
      </div>

      {/* Activity List */}
      <div
        className={cn(
          "grid",
          "transition-all duration-500 ease-[cubic-bezier(0.4,_0,_0.2,_1)]",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="px-2 pb-4">
            <div className="space-y-1">
              {items.map((activity, index) => (
                <div
                  key={activity.id}
                  className={cn(
                    "flex items-start gap-3 rounded-xl p-3",
                    "transition-all duration-500 ease-[cubic-bezier(0.4,_0,_0.2,_1)]",
                    "hover:bg-white/5",
                    activity.isRead ? "opacity-50" : "",
                    isOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                  )}
                  style={{
                    transitionDelay: isOpen ? `${index * 50}ms` : "0ms",
                  }}
                >
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-300",
                      activity.iconBg
                    )}
                  >
                    {activity.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-white/90 leading-snug">
                      {activity.title}
                    </h4>
                    <p className="text-xs text-white/40 mt-0.5">{activity.description}</p>

                    {activity.recommendation && (
                      <p className="text-[11px] text-amber-400/60 mt-1 leading-relaxed">
                        💡 {activity.recommendation}
                      </p>
                    )}

                    {(activity.severityBadge || activity.metricDisplay) && (
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {activity.severityBadge && (
                          <span
                            className={`text-[10px] px-1.5 py-0 rounded border ${activity.severityBadge.className}`}
                          >
                            {activity.severityBadge.label}
                          </span>
                        )}
                        {activity.metricDisplay && (
                          <span className="text-[11px] text-white/20">
                            <span className="text-red-400 dark:text-red-400/60">
                              {activity.metricDisplay.actual}
                            </span>
                            {" → "}
                            <span className="text-emerald-400 dark:text-emerald-400/50">
                              {t("monitoringAlerts.target", {
                                value: activity.metricDisplay.target,
                              })}
                            </span>
                          </span>
                        )}
                      </div>
                    )}

                    {activity.quickActions && activity.quickActions.length > 0 && (
                      <QuickActionsBar actions={activity.quickActions} />
                    )}

                    {activity.analysisPanel && (
                      <div onClick={(e) => e.stopPropagation()}>{activity.analysisPanel}</div>
                    )}

                    {(activity.feedbackPanel || activity.onMarkRead || activity.onDismiss) && (
                      <div
                        className="flex items-center justify-between mt-2.5 pt-2 border-t border-white/5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div>{activity.feedbackPanel}</div>
                        <div className="flex gap-1">
                          {activity.onMarkRead && !activity.isRead && (
                            <button
                              onClick={activity.onMarkRead}
                              className="flex items-center gap-1 text-white/25 hover:text-emerald-400 px-2 py-1 rounded-md hover:bg-white/5 transition-colors text-[11px]"
                            >
                              <Check className="w-3.5 h-3.5" />
                              {t("monitoringAlerts.markRead")}
                            </button>
                          )}
                          {activity.onDismiss && (
                            <button
                              onClick={activity.onDismiss}
                              className="flex items-center gap-1 text-white/25 hover:text-red-400 px-2 py-1 rounded-md hover:bg-white/5 transition-colors text-[11px]"
                            >
                              <X className="w-3.5 h-3.5" />
                              {t("monitoringAlerts.dismiss")}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-white/25 shrink-0 pt-0.5 whitespace-nowrap">
                    {activity.time}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mapping helpers for MonitoringAlert data ──

const ICON_MAP: Record<string, React.ElementType> = {
  cpl_exceeded: DollarSign,
  cpc_exceeded: DollarSign,
  roas_below: TrendingDown,
  no_leads: UserX,
  spend_anomaly: Activity,
  placement_waste: MapPin,
  zero_conversions: Unplug,
  budget_increase: TrendingUp,
  high_cpm: Gauge,
  early_winner_signal: Trophy,
  early_loser_signal: TriangleAlert,
  webinar_low_show_rate: Video,
  webinar_low_offer_rate: Video,
  shopify_low_stock: Package,
  shopify_out_of_stock: Package,
  shopify_revenue_drop: ShoppingCart,
  crm_low_close_rate: Handshake,
  crm_low_show_rate: UserX,
  crm_no_new_leads: UserX,
  baselinker_low_stock: Package,
  baselinker_revenue_drop: ShoppingCart,
  ringostat_low_answer_rate: Phone,
  ringostat_low_call_volume: Phone,
  allegro_sales_drop: Store,
  allegro_low_roas: Store,
  hubspot_low_close_rate: Handshake,
  hubspot_pipeline_stall: RefreshCcw,
};

const SEVERITY_ICON_BG: Record<string, string> = {
  critical: "bg-red-500/20 text-red-400",
  warning: "bg-yellow-500/20 text-yellow-400",
  info: "bg-blue-500/20 text-blue-400",
};

function formatTimeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "przed chwilą";
  if (diffMin < 60) return `${diffMin} min temu`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} godz. temu`;
  const diffDays = Math.floor(diffHours / 24);
  return diffDays === 1 ? "wczoraj" : `${diffDays} dni temu`;
}

export interface MonitoringAlertItem {
  id: string;
  alertType: string;
  severity: string;
  message: string;
  accountName: string | null;
  isRead: boolean;
  createdAt: string;
}

export function monitoringAlertsToItems(alerts: MonitoringAlertItem[]): ActivityItem[] {
  return alerts.map((alert) => {
    const IconComponent = ICON_MAP[alert.alertType] ?? AlertTriangle;
    const iconBg = SEVERITY_ICON_BG[alert.severity] ?? SEVERITY_ICON_BG.info;
    return {
      id: alert.id,
      icon: <IconComponent className="h-4 w-4" />,
      iconBg,
      title: alert.accountName ?? "Alert",
      description: alert.message,
      time: formatTimeAgo(alert.createdAt),
      isRead: alert.isRead,
    };
  });
}
